// src/components/NoticiasPlaceholder.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Pagination,
  Form,
  InputGroup,
  Alert,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import "./noticias-placeholder.css";
import NewsSkeletonGrid from "./NewsSkeletonGrid.jsx";

const API_URL = "https://facach.coop.hn/api";

// ─── Helper para construir números de página con ellipsis ────────────────────
function buildPageNumbers(page, totalPages) {
  const raw = new Set([1, totalPages, page - 1, page, page + 1]);
  const filtered = [...raw]
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < filtered.length; i++) {
    const curr = filtered[i];
    const prev = filtered[i - 1];
    if (prev !== undefined && curr - prev > 1)
      result.push(`ellipsis-${prev}-${curr}`);
    result.push(curr);
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
const NoticiasPlaceholder = ({
  title = "Noticias Recientes",
  initialPageSize = 16,
}) => {
  const [noticias, setNoticias]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(initialPageSize);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // ── Fetch cada vez que cambia página o tamaño ─────────────────────────────
  useEffect(() => {
    const fetchNoticias = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/news?page=${page}&limit=${pageSize}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.ok) {
          setNoticias(json.data);
          setTotal(json.pagination.total);
          setTotalPages(json.pagination.totalPages);
        }
      } catch (err) {
        console.error("Error cargando noticias:", err.message);
        setError("No se pudieron cargar las noticias.");
      } finally {
        setLoading(false);
      }
    };

    fetchNoticias();
  }, [page, pageSize]);

  // Resetear a página 1 al cambiar tamaño
  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const pageNumbers = useMemo(
    () => buildPageNumbers(page, totalPages),
    [page, totalPages]
  );

  const isEmpty  = !loading && !error && noticias.length === 0;
  const startIdx = (page - 1) * pageSize;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="noticias-placeholder-wrapper">
      <Container className="py-4">
        <h5 className="text-center mb-4">{title}</h5>

        {/* ── Controles superiores ─────────────────────────────────────────── */}
        <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 mb-3">
          <InputGroup style={{ maxWidth: 260 }}>
            <InputGroup.Text id="page-size-label">Por página</InputGroup.Text>
            <Form.Select
              aria-label="Items por página"
              aria-describedby="page-size-label"
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              disabled={loading}
            >
              <option value={8}>8</option>
              <option value={12}>12</option>
              <option value={16}>16</option>
              <option value={24}>24</option>
            </Form.Select>
          </InputGroup>

          <div
            className="ms-md-auto small text-muted d-flex align-items-center"
            aria-live="polite"
          >
            {loading ? (
              <span>Cargando…</span>
            ) : isEmpty ? (
              <span>No hay noticias disponibles</span>
            ) : (
              <span>
                Mostrando {startIdx + 1}–{startIdx + noticias.length} de {total}
              </span>
            )}
          </div>
        </div>

        {/* ── Contenido ────────────────────────────────────────────────────── */}
        {loading ? (
          <NewsSkeletonGrid count={pageSize} />
        ) : error ? (
          <Alert variant="danger" className="text-center">{error}</Alert>
        ) : isEmpty ? (
          <Alert variant="secondary" className="text-center">
            No hay contenido por mostrar.
          </Alert>
        ) : (
          <div className="news-card-grid">
            {noticias.map((n) => (
              <article key={n.id} className="news-card">
                <Link
                  to={`/noticias/${n.id}`}
                  className="news-card-media"
                  aria-label={`Ver detalle de ${n.title}`}
                >
                  {n.image_url ? (
                    <img
                      src={n.image_url}
                      alt={n.image_alt || n.title}
                      className="news-card-img"
                      loading="lazy"
                    />
                  ) : (
                    <div className="news-card-img-placeholder d-flex align-items-center justify-content-center bg-light">
                      <svg width="48" height="48" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#bdbdbd" d="M19 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2m0 16H5V5h14zM8 13l2.5 3.01L13 13l3 4H8zM9 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4" />
                      </svg>
                    </div>
                  )}
                </Link>
                <div className="news-card-body">
                  <h6 className="news-card-title">{n.title}</h6>
                  {n.summary && (
                    <p className="news-card-excerpt">{n.summary}</p>
                  )}
                </div>
                <div className="news-card-footer">
                  <Link to={`/noticias/${n.id}`} className="news-card-link">
                    Explora la Noticia Completa <span aria-hidden="true">➜</span>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* ── Paginación ───────────────────────────────────────────────────── */}
        {totalPages > 1 && !isEmpty && (
          <div className="d-flex justify-content-center mt-4">
            <Pagination className="mb-0" aria-label="Paginación de noticias">
              <Pagination.First
                onClick={() => setPage(1)}
                disabled={page === 1 || loading}
              />
              <Pagination.Prev
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
              />
              {pageNumbers.map((p) =>
                typeof p === "string" ? (
                  <Pagination.Ellipsis key={p} disabled aria-hidden="true" />
                ) : (
                  <Pagination.Item
                    key={p}
                    active={p === page}
                    onClick={() => setPage(p)}
                    aria-current={p === page ? "page" : undefined}
                    disabled={loading}
                  >
                    {p}
                  </Pagination.Item>
                )
              )}
              <Pagination.Next
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || loading}
              />
              <Pagination.Last
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages || loading}
              />
            </Pagination>
          </div>
        )}
      </Container>
    </div>
  );
};

export default NoticiasPlaceholder;